import { EmptyPriceBlock, GhostPriceBlock, PriceBlock, PriceBlockContainer, PriceWithDiscountBlock } from './styles';
import React from 'react';
import { formatCurrency } from 'utils/formatCurrency';
import { ACTION_STATUSES } from 'shared/consts';
import { Price } from 'store/price/types';
import { useIntl } from 'react-intl';

interface PriceBlockProps {
  showMinimumPrice: boolean;
  price: Price;
  minimumPrice: Price;
  priceStatus: ACTION_STATUSES;
  locale: string;
  showFreeLabel: boolean;
  hasContentBelow?: boolean;
}

export const PriceComponent = ({
  price,
  minimumPrice,
  priceStatus,
  locale,
  showMinimumPrice,
  showFreeLabel,
  hasContentBelow = true,
}: PriceBlockProps): JSX.Element => {
  const intl = useIntl();
  const hasDiscount = Boolean(price?.price_with_discount >= 0);
  const hasCredits = Boolean(price?.credit_total > 0);

  if (priceStatus === ACTION_STATUSES.PENDING) {
    return <GhostPriceBlock hasContentBelow={hasContentBelow} data-testid="ghost-price-block" />;
  }

  if (showMinimumPrice) {
    return (
      <PriceBlock data-testid="price-block" hasContentBelow={hasContentBelow}>
        {formatCurrency(minimumPrice?.price, minimumPrice?.currency, locale, false)}+
      </PriceBlock>
    );
  }

  if (price?.price || hasCredits) {
    return (
      <PriceBlockContainer>
        {price.price > 0 && (
          <>
            <PriceBlock hasDiscount={hasDiscount} hasContentBelow={hasContentBelow} data-testid="price-block">
              {formatCurrency(price.price, price.currency, locale)}
            </PriceBlock>
            {hasDiscount && (
              <PriceWithDiscountBlock hasContentBelow={hasContentBelow} data-testid="price-block">
                {formatCurrency(price.price_with_discount, price.currency, locale)}
              </PriceWithDiscountBlock>
            )}
          </>
        )}
        {hasCredits && (
          <PriceBlock hasContentBelow={hasContentBelow} data-testid="credits-block">
            {intl.formatMessage({ id: 'credits' }, { count: Math.ceil(price.credit_total) })}
          </PriceBlock>
        )}
      </PriceBlockContainer>
    );
  }

  if (showFreeLabel) {
    return (
      <PriceBlock hasContentBelow={hasContentBelow} data-testid="price-block">
        {intl.formatMessage({ id: 'free' })}
      </PriceBlock>
    );
  }

  return <EmptyPriceBlock data-testid="empty-price-block" />;
};
